import dayjs, { Dayjs } from 'dayjs';
import LOCALS from 'commons/locals';
import { OmsRecycleOrderDetail } from 'types/oms';
import { GlobalInfoState } from 'store/slices/globalInfoSlice';

export type ModifyRecycleOrderTimeFieldName =
  | 'createTime'
  | 'agreeFirstValuationTime'
  | 'agreeFinalValuationTime'
  | 'completeTime'
  | 'firstValuationTime'
  | 'finalValuationTime'
  | 'financialPaymentTime';

export const MODIFY_RECYCLE_ORDER_TIME_FIELDS: {
  name: ModifyRecycleOrderTimeFieldName;
  labelKey: string;
  getValue: (data: OmsRecycleOrderDetail) => string | undefined;
}[] = [
  {
    name: 'createTime',
    labelKey: LOCALS.created_time,
    getValue: (data) => data.omsRecycleOrder?.createTime,
  },
  {
    name: 'agreeFirstValuationTime',
    labelKey: LOCALS.intention_confirmation_time,
    getValue: (data) => data.omsRecycleOrderItem?.agreeFirstValuationTime,
  },
  {
    name: 'agreeFinalValuationTime',
    labelKey: LOCALS.contract_confirmation_time,
    getValue: (data) => data.omsRecycleOrderItem?.agreeFinalValuationTime,
  },
  {
    name: 'completeTime',
    labelKey: LOCALS.transaction_completed_time,
    getValue: (data) => data.omsRecycleOrder?.completeTime,
  },
  {
    name: 'firstValuationTime',
    labelKey: LOCALS.initial_appraisal_time,
    getValue: (data) => data.omsRecycleOrderItem?.firstValuationTime,
  },
  {
    name: 'finalValuationTime',
    labelKey: LOCALS.quotation_confirmation_time,
    getValue: (data) => data.omsRecycleOrderItem?.finalValuationTime,
  },
  {
    name: 'financialPaymentTime',
    labelKey: LOCALS.payment_time,
    getValue: (data) => data.omsRecycleOrder?.financialPaymentTime,
  },
];

export type ModifyBaseInfoFormValues = {
  isTradeIn: number;
  firstValuationShopRemark?: string;
  staffName?: string;
  receptionistId?: number;
  receptionistLabel?: string;
};

export type ModifyRecycleOrderTimesFormValues = Partial<
  Record<ModifyRecycleOrderTimeFieldName, Dayjs>
>;

export const parseExistingTime = (value?: string) => {
  if (!value) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : undefined;
};

type StaffSelectOption = GlobalInfoState['staffSelectOptions'][number];

const RECEPTIONIST_LABEL_KEYS = [
  'receptionistName',
  'receptionistNickName',
  'receptionStaffName',
  'receptionistLabel',
] as const;

const getReceptionistLabelFromOrder = (
  order?: OmsRecycleOrderDetail['omsRecycleOrder'],
) => {
  if (!order) return undefined;

  const rawOrder = order as OmsRecycleOrderDetail['omsRecycleOrder'] &
    Partial<Record<(typeof RECEPTIONIST_LABEL_KEYS)[number], unknown>>;

  const matchedKey = RECEPTIONIST_LABEL_KEYS.find((key) => {
    const value = rawOrder[key];
    return typeof value === 'string' && value.trim();
  });

  if (!matchedKey) return undefined;

  return String(rawOrder[matchedKey]).trim();
};

export const getModifyBaseInfoInitialValues = (
  data: OmsRecycleOrderDetail,
): ModifyBaseInfoFormValues => {
  return {
    isTradeIn: data.omsRecycleOrder?.isTradeIn === 1 ? 1 : 0,
    firstValuationShopRemark:
      data.omsRecycleOrderItem?.firstValuationShopRemark ?? '',
    staffName: data.omsRecycleOrder?.staffName,
    receptionistId: data.omsRecycleOrder?.receptionistId,
    receptionistLabel: getReceptionistLabelFromOrder(data.omsRecycleOrder),
  };
};

export const getModifyRecycleOrderTimesInitialValues = (
  data: OmsRecycleOrderDetail,
): ModifyRecycleOrderTimesFormValues => {
  return MODIFY_RECYCLE_ORDER_TIME_FIELDS.reduce<ModifyRecycleOrderTimesFormValues>(
    (acc, field) => {
      const parsed = parseExistingTime(field.getValue(data));
      if (parsed) {
        acc[field.name] = parsed;
      }
      return acc;
    },
    {},
  );
};

export const buildModifyBaseInfoPayload = (
  values: ModifyBaseInfoFormValues,
  omsRecycleOrderId: string,
) => {
  return {
    omsRecycleOrderId,
    isTradeIn: values.isTradeIn,
    firstValuationShopRemark: values.firstValuationShopRemark?.trim() || '',
    staffName: values.staffName,
    receptionistId: values.receptionistId,
  };
};

export const buildModifyRecycleOrderTimesPayload = (
  values: ModifyRecycleOrderTimesFormValues,
  omsRecycleOrderId: string,
  initialValues?: ModifyRecycleOrderTimesFormValues,
) => {
  const timePayload = MODIFY_RECYCLE_ORDER_TIME_FIELDS.reduce<
    Partial<Record<ModifyRecycleOrderTimeFieldName, string>>
  >((acc, field) => {
    const value = values[field.name];
    const initialValue = initialValues?.[field.name];
    if (!value || !initialValue) {
      return acc;
    }
    const next = dayjs(value);
    const prev = dayjs(initialValue);
    if (!next.isValid() || next.isSame(prev, 'minute')) {
      return acc;
    }
    acc[field.name] = next.toISOString();
    return acc;
  }, {});

  return {
    omsRecycleOrderId,
    ...timePayload,
  };
};

export const getSelectableStaffOptions = (
  staffSelectOptions: StaffSelectOption[],
  currentValues?: Pick<
    ModifyBaseInfoFormValues,
    'receptionistId' | 'receptionistLabel'
  >,
  currentShopIdList: number[] = [],
) => {
  const activeOptions = staffSelectOptions.reduce<
    { value: string | number; label: string }[]
  >((acc, option) => {
    if (option.status !== 1) return acc;
    if (
      currentShopIdList.length &&
      !(option.shop && currentShopIdList.includes(option.shop)) &&
      !(
        option.subShop &&
        option.subShop.some((shopId) => currentShopIdList.includes(shopId))
      )
    ) {
      return acc;
    }
    acc.push({ value: option.value, label: option.label });
    return acc;
  }, []);

  const hasCurrentSelection = currentValues?.receptionistId !== undefined;
  if (!hasCurrentSelection) return activeOptions;

  const currentOption = staffSelectOptions.find(
    (option) => option.value === currentValues!.receptionistId,
  );

  if (
    currentOption &&
    activeOptions.some((option) => option.value === currentOption.value)
  ) {
    return activeOptions;
  }

  const fallbackLabel =
    currentValues?.receptionistLabel || currentOption?.label;
  if (!fallbackLabel) return activeOptions;

  return [
    ...activeOptions,
    {
      value:
        currentValues?.receptionistId ?? currentOption?.value ?? fallbackLabel,
      label: fallbackLabel,
    },
  ];
};
